import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  MessageSquare,
  CheckSquare,
  Clock,
  UserPlus,
  Loader2,
  AlertCircle
} from 'lucide-react'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { StatTile, StatTileTone } from '@/components/ui/StatTile'
import MenteePlansSummary from '@/components/plans/MenteePlansSummary'
import { useAuth } from '@/contexts/Auth0Context'
import { taskService } from '@/services/taskService'
import { matchingService } from '@/services/matchingService'
import { messageService } from '@/services/messageService'
import { Task } from '@/types/task'
import { MatchingRequest, Relationship, RelationshipStatus } from '@/types/matching'
import { Conversation } from '@/types/message'
import RateRelationshipModal from '@/components/matching/RateRelationshipModal'
import { useToast } from '@/contexts/ToastContext'
import { dismissRatingPrompt, findRelationshipNeedingRating } from '@/utils/ratingPrompt'

interface DashboardStats {
  activeMentees: number
  pendingRequests: number
  completedTasks: number
  underReviewTasks: number
}

const MentorDashboard: React.FC = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    activeMentees: 0,
    pendingRequests: 0,
    completedTasks: 0,
    underReviewTasks: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([])
  const [tasksToReview, setTasksToReview] = useState<Task[]>([])
  const [pendingRequests, setPendingRequests] = useState<MatchingRequest[]>([])
  const [ratingRelationship, setRatingRelationship] = useState<Relationship | null>(null)
  const [isRatingSubmitting, setIsRatingSubmitting] = useState(false)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)

        const [taskStatsResult, relationshipsResult, requestsResult, conversationsResult, tasksResponseResult] =
          await Promise.allSettled([
            taskService.getTaskStats(),
            matchingService.getMyRelationships(),
            matchingService.getPendingRequests(),
            messageService.getConversations(),
            taskService.getMyTasks({ status: 'under_review', limit: 5 })
          ])

        for (const [label, result] of [
          ['task stats', taskStatsResult],
          ['relationships', relationshipsResult],
          ['pending requests', requestsResult],
          ['conversations', conversationsResult],
          ['tasks under review', tasksResponseResult]
        ] as const) {
          if (result.status === 'rejected') {
            console.error(`Failed to load ${label}:`, result.reason)
          }
        }

        const taskStats =
          taskStatsResult.status === 'fulfilled'
            ? taskStatsResult.value
            : { total: 0, pending: 0, inProgress: 0, underReview: 0, completed: 0, overdue: 0, avgProgress: 0 }
        const relationships = relationshipsResult.status === 'fulfilled' ? relationshipsResult.value : []
        const requests = requestsResult.status === 'fulfilled' ? requestsResult.value : []
        const conversations = conversationsResult.status === 'fulfilled' ? conversationsResult.value : []
        const tasksResponse = tasksResponseResult.status === 'fulfilled' ? tasksResponseResult.value : []

        if ([taskStatsResult, relationshipsResult, requestsResult, conversationsResult, tasksResponseResult].every(
          (result) => result.status === 'rejected'
        )) {
          setError('Failed to load dashboard data. Please try again.')
        }

        const activeRelationships = relationships.filter(
          (r) => r.status === RelationshipStatus.ACTIVE
        )

        setStats({
          activeMentees: activeRelationships.length,
          pendingRequests: requests.length,
          completedTasks: taskStats.completed,
          underReviewTasks: taskStats.underReview
        })

        setRecentConversations(conversations.slice(0, 3))
        setTasksToReview(tasksResponse.slice(0, 3))
        setPendingRequests(requests.slice(0, 3))

        if (user) {
          setRatingRelationship(findRelationshipNeedingRating(relationships, user.id, 'mentor'))
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err)
        setError('Failed to load dashboard data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const handleCloseRatingPrompt = () => {
    if (user && ratingRelationship) {
      dismissRatingPrompt(user.id, ratingRelationship.id)
    }
    setRatingRelationship(null)
  }

  const handleSubmitRating = async (rating: number, review?: string) => {
    if (!ratingRelationship) return

    try {
      setIsRatingSubmitting(true)
      await matchingService.rateRelationship(ratingRelationship.id, { rating, review })
      showToast({ type: 'success', message: 'Rating submitted!' })
      setRatingRelationship(null)
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to submit rating'
      })
    } finally {
      setIsRatingSubmitting(false)
    }
  }

  const statCards: { title: string; value: string | number; icon: typeof Users; tone: StatTileTone }[] = [
    {
      title: 'Active Mentees',
      value: stats.activeMentees,
      icon: Users,
      tone: 'accent'
    },
    {
      title: 'Pending Requests',
      value: stats.pendingRequests,
      icon: UserPlus,
      tone: 'warning'
    },
    {
      title: 'Completed Tasks',
      value: stats.completedTasks,
      icon: CheckSquare,
      tone: 'success'
    },
    {
      title: 'Tasks to Review',
      value: stats.underReviewTasks,
      icon: Clock,
      tone: 'primary'
    }
  ]

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }

  const formatDueDate = (dateString?: string): string => {
    if (!dateString) return 'No due date'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / 86400000)

    if (diffDays < 0) return 'Overdue'
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return 'Due tomorrow'
    return `Due in ${diffDays} days`
  }

  const getDueDateColor = (dateString?: string): string => {
    if (!dateString) return 'bg-gray-50'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffMs / 86400000)

    if (diffDays < 0) return 'bg-red-50'
    if (diffDays <= 1) return 'bg-yellow-50'
    if (diffDays <= 3) return 'bg-accent-50'
    return 'bg-green-50'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-gray-900 font-medium">{error}</p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="mt-2 text-gray-600">
            Here's an overview of your mentoring activities
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <StatTile key={stat.title} title={stat.title} value={stat.value} icon={stat.icon} tone={stat.tone} />
          ))}
        </div>

        {/* Mentee plans summary */}
        <div className="mb-8">
          <MenteePlansSummary />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activities */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Activities
              </h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {recentConversations.length === 0 && pendingRequests.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No recent activities
                  </p>
                ) : (
                  <>
                    {recentConversations.map((conv) => (
                      <div key={conv.relationshipId} className="flex items-start space-x-3">
                        <div className="bg-accent-100 p-2 rounded-full">
                          <MessageSquare className="w-4 h-4 text-accent-700" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {conv.unreadCount > 0
                              ? `${conv.unreadCount} new message${conv.unreadCount > 1 ? 's' : ''} from ${conv.participant.firstName} ${conv.participant.lastName}`
                              : `Message from ${conv.participant.firstName} ${conv.participant.lastName}`}
                          </p>
                          {conv.lastMessage && (
                            <p className="text-sm text-gray-500">
                              {formatRelativeTime(conv.lastMessage.createdAt)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    {pendingRequests.map((request) => (
                      <div key={request.id} className="flex items-start space-x-3">
                        <div className="bg-orange-100 p-2 rounded-full">
                          <UserPlus className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            New mentee request from {request.mentee?.firstName} {request.mentee?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatRelativeTime(request.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <div className="mt-6">
                <Button variant="outline" className="w-full" onClick={() => navigate('/mentees')}>
                  View All Activities
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Upcoming Tasks */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">
                Tasks to Review
              </h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                {tasksToReview.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No tasks waiting for review
                  </p>
                ) : (
                  tasksToReview.map((task) => (
                    <div
                      key={task.id}
                      className={`flex items-center justify-between p-4 ${getDueDateColor(task.dueDate)} rounded-2xl`}
                    >
                      <div>
                        <p className="font-medium text-gray-900">{task.title}</p>
                        <p className="text-sm text-gray-500">
                          {formatDueDate(task.dueDate)}
                          {task.mentee && ` • ${task.mentee.firstName} ${task.mentee.lastName}`}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/tasks/${task.id}`)}>
                        Review
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-6">
                <Button variant="primary" className="w-full" onClick={() => navigate('/tasks')}>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  View All Tasks
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button variant="primary" className="justify-center" onClick={() => navigate('/mentees')}>
              <UserPlus className="w-4 h-4 mr-2" />
              Review Requests
            </Button>
            <Button variant="outline" className="justify-center" onClick={() => navigate('/messages')}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Send Message
            </Button>
            <Button variant="outline" className="justify-center" onClick={() => navigate('/tasks?create=1')}>
              <CheckSquare className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          </div>
        </div>
      </div>

      {/* Rating prompt for a completed relationship */}
      {ratingRelationship && (
        <RateRelationshipModal
          relationship={ratingRelationship}
          userRole="mentor"
          isOpen={!!ratingRelationship}
          onClose={handleCloseRatingPrompt}
          onSubmit={handleSubmitRating}
          isSubmitting={isRatingSubmitting}
        />
      )}
    </div>
  )
}

export default MentorDashboard